import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { BookOpen, LineChart, MessageCircle, NotebookPen, LogOut, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const tabs = [
  { to: "/app/checkin", label: "Check-in", icon: NotebookPen, testid: "nav-checkin" },
  { to: "/app/insights", label: "Insights", icon: LineChart, testid: "nav-insights" },
  { to: "/app/companion", label: "Companion", icon: MessageCircle, testid: "nav-companion" },
  { to: "/app/library", label: "Library", icon: BookOpen, testid: "nav-library" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen pb-28 md:pb-10">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FAF6F0]/85 border-b border-[#EAE0D2]/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <NavLink to="/app/checkin" className="flex items-center gap-3" data-testid="brand-link">
            <span className="leaf-mark" />
            <span className="font-serif-display text-2xl text-[#2C221E]">Meridia</span>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm transition-colors ${
                    isActive
                      ? "bg-[#F4EDE2] text-[#2C221E]"
                      : "text-[#52443E] hover:text-[#2C221E] hover:bg-[#F4EDE2]/60"
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#73635B]" data-testid="user-greeting">
              {user?.name}
            </span>
            <NavLink
              to="/app/settings"
              className="p-2 rounded-full border border-[#D8CBB9] hover:bg-[#F4EDE2] transition-colors"
              aria-label="Settings"
              data-testid="nav-settings"
            >
              <SettingsIcon size={16} />
            </NavLink>
            <button
              onClick={onLogout}
              className="p-2 rounded-full border border-[#D8CBB9] hover:bg-[#F4EDE2] transition-colors"
              aria-label="Sign out"
              data-testid="logout-button"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FAF6F0]/95 backdrop-blur-md border-t border-[#EAE0D2]">
        <div className="grid grid-cols-4 max-w-md mx-auto">
          {tabs.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 gap-1 text-xs transition-colors ${
                  isActive ? "text-[#C26D53]" : "text-[#73635B]"
                }`
              }
            >
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
